import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Checkbox } from '@givebutter/primitives/checkbox';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { DATE_MASK_FORMAT } from '@givebutter/util';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { updateTransaction } from 'api/transactions';
import * as ReadOnlyPaymentDetails from 'assets/js/pages/transactions/ReadOnlyPaymentDetails';
import { TransactionMethod } from 'assets/js/pages/transactions/TransactionMethod';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { maskedDateToServerDateTime, rawDateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { TransactionMethod as ITransactionMethod } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface PaymentMethodForm {
    type: ITransactionMethod;
    checkNumber: string;
    checkDepositDate: string;
    offlinePaymentReceived: boolean;
}

const TransactionEditablePaymentMethod = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId, timezone } = useCurrentAccount();
    const { data: transaction } = useCurrentTransactionOrById();
    const hasNoPaymentMethod = !transaction?.method || transaction?.method === 'none';
    const isEditable = isPaymentEditableByMethod(transaction?.method!);
    const { toggleCheckReceived = false } = useFlags();

    const methods = Form.useForm<PaymentMethodForm>({
        values: {
            type: transaction?.method ?? 'cash',
            checkNumber: transaction?.check_number ?? '',
            checkDepositDate: transaction?.check_deposited_at
                ? rawDateFromServer(transaction.check_deposited_at)
                      .setZone(timezone)
                      .toFormat(DATE_MASK_FORMAT)
                : '',
            offlinePaymentReceived: !!transaction?.offline_payment_received_at,
        },
    });
    const [isOpen, setIsOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { reset, watch, control, path, handleSubmit, resetFormButton } = methods;

    const paymentType = watch('type');
    const isCheck = paymentType === 'check';
    const isCash = paymentType === 'cash';

    const { isPending: isSubmitting, mutate: submit } = useMutation({
        mutationFn: (values: PaymentMethodForm) => {
            const isCheck = values.type === 'check';
            const isCash = values.type === 'cash';

            return updateTransaction(transaction?.id!, {
                method: values.type,
                check_number: isCheck ? values.checkNumber : null,
                check_deposited_at:
                    isCheck && values.checkDepositDate.length > 0
                        ? maskedDateToServerDateTime(values.checkDepositDate)
                        : null,
                ...(toggleCheckReceived && {
                    offline_payment_received:
                        isCheck || isCash ? values.offlinePaymentReceived : null,
                }),
            });
        },
        onSuccess: async () => {
            toast.success('Successfully updated transaction payment method!');
            setIsEditing(false);
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Failed to update transaction payment method');
            resetFormButton();
        },
    });

    if (hasNoPaymentMethod) {
        return null;
    }

    return (
        <Collapsible.Root open={isOpen} onOpenChange={setIsOpen} className={'group'}>
            <Form.Provider {...methods}>
                <form
                    className={'contents'}
                    onSubmit={handleSubmit(submit as SubmitHandler<PaymentMethodForm>)}
                >
                    <div className={'flex flex-col gap-2'}>
                        <Form.Label name={path('type')} className={'text-gray-800'}>
                            Payment details
                        </Form.Label>
                        <Collapsible.Trigger>
                            <div className={'flex items-center gap-2'}>
                                <FontAwesomeIcon
                                    className={'group-radix-state-open:rotate-90 text-gray-600'}
                                    icon={faChevronRight}
                                />
                                <TransactionMethod />
                                {isEditable && !isEditing && (
                                    <Button
                                        variant="none"
                                        size="sm"
                                        onClick={e => {
                                            if (isOpen) {
                                                e.preventDefault();
                                            }

                                            setIsEditing(true);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            className={'text-gray-400'}
                                            icon={faEdit}
                                        />
                                    </Button>
                                )}
                            </div>
                        </Collapsible.Trigger>
                        <Collapsible.Content className={'bg-gray-25 rounded-lg p-3'}>
                            {isEditing ? (
                                <div className={'flex flex-col gap-2'}>
                                    <Form.Container>
                                        <Form.Label name={path('type')}>Payment method</Form.Label>
                                        <Form.SingleSelect control={control} name="type">
                                            <SingleSelect.Trigger aria-label="Payment method" />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="cash">
                                                    <SingleSelect.ItemText>
                                                        Cash
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="check">
                                                    <SingleSelect.ItemText>
                                                        Check
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="in-kind">
                                                    <SingleSelect.ItemText>
                                                        In-kind
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="stock">
                                                    <SingleSelect.ItemText>
                                                        Stock
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="property">
                                                    <SingleSelect.ItemText>
                                                        Property
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="other">
                                                    <SingleSelect.ItemText>
                                                        Other
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                    </Form.Container>
                                    {isCheck && (
                                        <>
                                            <Form.Container>
                                                <Form.Label
                                                    name={path('checkNumber')}
                                                    className={'text-gray-800'}
                                                >
                                                    Check number
                                                </Form.Label>
                                                <Form.TextInputRoot
                                                    control={control}
                                                    name={'checkNumber'}
                                                >
                                                    <Form.TextInputForm />
                                                </Form.TextInputRoot>
                                            </Form.Container>
                                            <Form.Container>
                                                <Form.Label
                                                    name={path('checkDepositDate')}
                                                    className={'text-gray-800'}
                                                >
                                                    Deposit date
                                                </Form.Label>
                                                <Form.TextInputRoot
                                                    control={control}
                                                    name={'checkDepositDate'}
                                                    inputRootProps={{
                                                        mask: 'date',
                                                    }}
                                                >
                                                    <Form.TextInputForm
                                                        placeholder={'mm/dd/yyyy'}
                                                    />
                                                </Form.TextInputRoot>
                                            </Form.Container>
                                            {toggleCheckReceived && (
                                                <Form.Container>
                                                    <div className="flex flex-row justify-items-start gap-2">
                                                        <Form.UseFormCheckboxInput
                                                            control={control}
                                                            name="offlinePaymentReceived"
                                                            id="offlinePaymentReceived"
                                                        />
                                                        <div className="flex flex-col gap-0.5">
                                                            <Form.Label
                                                                name={path(
                                                                    'offlinePaymentReceived',
                                                                )}
                                                                className="leading-none"
                                                            >
                                                                Check received
                                                                <p className="text-sm font-normal">
                                                                    Marks pending check as a
                                                                    succeeded payment.
                                                                </p>
                                                            </Form.Label>
                                                        </div>
                                                    </div>
                                                    <Form.Error
                                                        name={path('offlinePaymentReceived')}
                                                    />
                                                </Form.Container>
                                            )}
                                        </>
                                    )}
                                    {paymentType === 'cash' && toggleCheckReceived && (
                                        <Form.Container>
                                            <div className="flex flex-row justify-items-start gap-2">
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name="offlinePaymentReceived"
                                                    id="offlinePaymentReceived"
                                                />
                                                <div className="flex flex-col gap-0.5">
                                                    <Form.Label
                                                        name={path('offlinePaymentReceived')}
                                                        className="leading-none"
                                                    >
                                                        Cash received
                                                        <p className="text-sm font-normal">
                                                            Marks pending cash payment as a
                                                            succeeded payment.
                                                        </p>
                                                    </Form.Label>
                                                </div>
                                            </div>
                                            <Form.Error name={path('offlinePaymentReceived')} />
                                        </Form.Container>
                                    )}
                                    <div className={'flex gap-2 self-end'}>
                                        <Button
                                            variant={'outline'}
                                            color={'neutral'}
                                            onClick={() => {
                                                setIsEditing(false);
                                                reset();
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Form.SubmitButton isSubmitting={isSubmitting}>
                                            Save
                                        </Form.SubmitButton>
                                    </div>
                                </div>
                            ) : (
                                <ReadOnlyPaymentDetails.Root transaction={transaction}>
                                    <ReadOnlyPaymentDetails.Name />
                                    <ReadOnlyPaymentDetails.Phone />
                                    <ReadOnlyPaymentDetails.Address />
                                    <ReadOnlyPaymentDetails.Email />
                                    <ReadOnlyPaymentDetails.Method />
                                    <ReadOnlyPaymentDetails.CcExpire />
                                    <ReadOnlyPaymentDetails.Daf />
                                    <ReadOnlyPaymentDetails.Company />
                                    {isCheck && (
                                        <>
                                            <ReadOnlyPaymentDetails.CheckNumber />
                                            {!transaction.check_number && (
                                                <div className={'flex items-center gap-2'}>
                                                    <ReadOnlyPaymentDetails.Label>
                                                        <p>Check number</p>
                                                    </ReadOnlyPaymentDetails.Label>
                                                    <Button
                                                        variant="none"
                                                        color="primary"
                                                        size="sm"
                                                        onClick={() => setIsEditing(true)}
                                                        className="font-medium"
                                                    >
                                                        Add +
                                                    </Button>
                                                </div>
                                            )}
                                            <ReadOnlyPaymentDetails.CheckDepositedDate />
                                            {!transaction.check_deposited_at && (
                                                <div className={'flex items-center gap-2'}>
                                                    <ReadOnlyPaymentDetails.Label>
                                                        <p>Deposit date</p>
                                                    </ReadOnlyPaymentDetails.Label>
                                                    <Button
                                                        variant="none"
                                                        color="primary"
                                                        size="sm"
                                                        onClick={() => setIsEditing(true)}
                                                        className="font-medium"
                                                    >
                                                        Add +
                                                    </Button>
                                                </div>
                                            )}
                                            {toggleCheckReceived && (
                                                <Form.Container>
                                                    <div className="flex flex-row gap-2">
                                                        <Checkbox
                                                            disabled
                                                            checked={
                                                                !!transaction.offline_payment_received_at
                                                            }
                                                        />
                                                        <div className="flex flex-col gap-0.5">
                                                            <p className="text-sm font-medium leading-none">
                                                                Check received
                                                            </p>
                                                            <p className="text-sm font-normal">
                                                                Marks pending check as a succeeded
                                                                payment.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Form.Container>
                                            )}
                                        </>
                                    )}
                                    {isCash && toggleCheckReceived && (
                                        <Form.Container>
                                            <div className="flex flex-row gap-2">
                                                <Checkbox
                                                    disabled
                                                    checked={
                                                        !!transaction.offline_payment_received_at
                                                    }
                                                />
                                                <div className="flex flex-col gap-0.5">
                                                    <p className="text-sm font-medium leading-none">
                                                        Cash received
                                                    </p>
                                                    <p className="text-sm font-normal">
                                                        Marks pending cash payment as a succeeded
                                                        payment.
                                                    </p>
                                                </div>
                                            </div>
                                        </Form.Container>
                                    )}
                                </ReadOnlyPaymentDetails.Root>
                            )}
                        </Collapsible.Content>
                    </div>
                </form>
            </Form.Provider>
        </Collapsible.Root>
    );
};

const isPaymentEditableByMethod = (method: ITransactionMethod) => {
    switch (method) {
        case 'cash':
        case 'check':
        case 'in-kind':
        case 'stock':
        case 'property':
        case 'other':
            return true;
        case 'ach':
        case 'card':
        case 'digital_wallet':
        case 'paypal':
        case 'venmo':
        case 'terminal':
        case 'donor_advised_fund':
        default:
            return false;
    }
};

export { TransactionEditablePaymentMethod };
